import { RefObject, useRef } from "react";
import {
    AbortableRequestsContext,
    AbortableRequestsContextType,
} from "./AbortableRequestsContext";
import { QueryResult, OperationVariables } from "@apollo/client";

export const AbortableRequestsProvider: React.FCWithChildren = ({
    children,
}) => {
    const abortControllers = useRef<{
        [key: string]: RefObject<AbortController | null>;
    }>({});
    const paused = useRef<{ [key: string]: { [key: string]: boolean } }>({});
    const timeouts = useRef<{ [key: string]: ReturnType<typeof setTimeout> }>(
        {},
    );

    const fireRequestImmediately = (
        channel: string,
        id: string,
        fireRequest: () => Promise<QueryResult<any, OperationVariables>>,
        abortControllerRef: RefObject<AbortController | null>,
    ) => {
        const key = `${channel}-${id}`;
        fireRequest();
        abortControllers.current[key] = abortControllerRef;
    };

    const fireRequestWithDelay = (
        channel: string,
        id: string,
        fireRequest: () => Promise<QueryResult<any, OperationVariables>>,
        abortControllerRef: RefObject<AbortController | null>,
    ) => {
        const key = `${channel}-${id}`;
        clearTimeout(timeouts.current[key]);
        timeouts.current[key] = setTimeout(() => {
            if (!Object.keys(paused.current[key] || {}).length)
                fireRequestImmediately(
                    channel,
                    id,
                    fireRequest,
                    abortControllerRef,
                );
            else
                fireRequestWithDelay(
                    channel,
                    id,
                    fireRequest,
                    abortControllerRef,
                );
        }, 100);
    };

    const setAbortableRequest: AbortableRequestsContextType["setAbortableRequest"] =
        (channel, id, fireRequest, abortControllerRef) => {
            const key = `${channel}-${id}`;
            if (Object.keys(paused.current[key] || {}).length)
                fireRequestWithDelay(
                    channel,
                    id,
                    fireRequest,
                    abortControllerRef,
                );
            else
                fireRequestImmediately(
                    channel,
                    id,
                    fireRequest,
                    abortControllerRef,
                );
        };

    const abortPendingRequest: AbortableRequestsContextType["abortPendingRequest"] =
        (channel, id, reason = "Cancelling pending request") => {
            abortControllers.current[`${channel}-${id}`]?.current?.abort(
                reason,
            );
        };

    const pauseIncomingRequests = (
        channel: string,
        id: string,
        identifier: string,
    ) => {
        const key = `${channel}-${id}`;
        paused.current[key] = paused.current[key] || {};
        paused.current[key][identifier] = true;
    };

    const resumeIncomingRequests = (
        channel: string,
        id: string,
        identifier: string,
    ) => {
        const key = `${channel}-${id}`;
        delete paused.current[key][identifier];
    };

    return (
        <AbortableRequestsContext.Provider
            value={{
                setAbortableRequest,
                abortPendingRequest,
                pauseIncomingRequests,
                resumeIncomingRequests,
            }}
        >
            {children}
        </AbortableRequestsContext.Provider>
    );
};
